import { Dispatch, FC, SetStateAction } from 'react';
import { useStore } from 'hooks/useRootStore';
import { useMutation } from 'react-query';
import { useStyles } from './styles';
import { useForm, Controller } from 'react-hook-form';
import CloseIcon from '@material-ui/icons/Close';
import { TextField, Button, Grid, Modal, Paper, IconButton, FormControl, MenuItem } from '@material-ui/core';
import { uid } from 'utils';
import { IUserChangeModel } from 'components/Tables/UserEditRequestTable/models/userEditRequest.model';
import { handleCompanyUrl } from 'utils/organizationUtils';
import { useHistory } from 'react-router-dom';
import { useAppPreloader } from 'hooks/useAppPreloader';
import MuiPhoneNumber from 'material-ui-phone-number-2';
export interface UserNewRequestForm {
  data: IUserChangeModel[];
  open: boolean;
  handleClose: () => void;
  newUserId: number;
  rejectNewUser: boolean;
  setRejectNewUser: Dispatch<SetStateAction<boolean>>;
}
export const UserNewRequestDataForm: FC<UserNewRequestForm> = ({
  data,
  open,
  handleClose,
  newUserId,
  rejectNewUser,
  setRejectNewUser,
}) => {
  const classes = useStyles();
  const history = useHistory();
  const {
    userEditRequestTableStore: { updateApproveUser, rejectNewUserApi },
    notificationStore: { openNotification },
  } = useStore();

  const updateApproveUserReq = useMutation((data: any) => updateApproveUser(newUserId, data), {
    mutationKey: 'updateUserEditRequestReq',
    onSuccess: () => {
      handleClose();
      openNotification('success', `Successfully Accepted`);

      history.push(handleCompanyUrl('/admin/users-edit-request'));
    },
    onError: (error: any) => {
      const errorMessage = error.response?.data?.message || 'Something went wrong! Please try again.';
      openNotification('error', errorMessage);
    },
  });

  const rejectNewUserApiReq = useMutation((data: any) => rejectNewUserApi(newUserId, data), {
    mutationKey: 'updateUserEditRequestReq',
    onSuccess: () => {
      openNotification('success', `Successfully Rejected`);
      handleClose();
      history.push(handleCompanyUrl('/admin/users-edit-request'));
    },
    onError: (error: any) => openNotification('error', error),
  });
  const { control, handleSubmit, reset } = useForm<any>();
  const onSubmit = (SubmittedObject: any) => {
    if (rejectNewUser) {
      rejectNewUserApiReq.mutate({ reason: SubmittedObject['reason'] });
    } else {
      const updatedData = data
        .filter((list: IUserChangeModel) => list.field !== 'customAttribute')
        .map((list: IUserChangeModel) => ({
          ...list,
          newValue: SubmittedObject[list.field] || list.newValue,
        }));
      let updatedCustomAttributes: any[] = [];
      const customAttributeField = data.find((list: IUserChangeModel) => list.field === 'customAttribute');
      if (customAttributeField && Array.isArray(customAttributeField.customAttributes)) {
        updatedCustomAttributes = customAttributeField.customAttributes.map((attr) => ({
          label: attr.Field.label,
          name: attr.Field.label,
          type: attr.Field.type,
          options: attr.Field.options || [],
          value: SubmittedObject[attr.Field.label] || attr.Field.oldValue || '',
        }));
      }
      updateApproveUserReq.mutate({
        data: updatedData,
        customAttribute: updatedCustomAttributes,
      });
    }
  };
  useAppPreloader(rejectNewUserApiReq.isLoading || updateApproveUserReq.isLoading);
  return (
    <Modal
      className={classes.modal}
      disablePortal
      disableEnforceFocus
      disableAutoFocus
      open={open}
      style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
    >
      <Paper
        className={classes.paper}
        elevation={3}
        style={{
          overflowY: 'auto',
          maxHeight: '90vh',
          paddingTop: '10px',
        }}
      >
        <Grid container justify='space-between' alignItems='center'>
          {rejectNewUser ? (
            <p className={classes.label}>Reject New User Request</p>
          ) : (
            <p className={classes.label}>Edit New User Requests</p>
          )}
          <IconButton
            style={{ marginRight: -16 }}
            onClick={() => {
              reset();
              setRejectNewUser(false);
              handleClose();
            }}
          >
            <CloseIcon color='primary' />
          </IconButton>
        </Grid>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Grid key={uid()} container justify='space-between'>
            {data.map((item, index) =>
              item.field === 'phone' ? (
                <Controller
                  key={'Phone'}
                  name={item.field}
                  defaultValue={item.oldValue}
                  control={control}
                  render={({ field, fieldState: { invalid } }) => (
                    <FormControl fullWidth style={{ marginBottom: '10px' }}>
                      <MuiPhoneNumber
                        {...field}
                        disabled={rejectNewUser}
                        defaultValue={item.oldValue}
                        value={item.oldValue}
                        label={'phone'}
                        onChange={field.onChange}
                        className={classes.textField}
                        error={invalid}
                        helperText={invalid ? 'Required field' : ''}
                        data-cy='user-phone'
                        defaultCountry={'us'}
                        variant='standard'
                        color='warning'
                      />
                    </FormControl>
                  )}
                />
              ) : item.field === 'customAttribute' && Array.isArray(item.customAttributes) ? (
                item.customAttributes.map((attr, i) => (
                  <Controller
                    key={i}
                    name={attr.Field.label}
                    defaultValue={attr.Field.oldValue}
                    control={control}
                    render={({ field, fieldState: { invalid } }) =>
                      attr.Field.options && attr.Field.options.length > 0 ? (
                        <TextField
                          {...field}
                          select
                          className={classes.textField}
                          label={attr.Field.label}
                          onChange={field.onChange}
                          error={invalid}
                          disabled={rejectNewUser}
                          helperText={invalid ? 'Required field' : ''}
                        >
                          {attr.Field.options.map((option, index) => (
                            <MenuItem key={index} value={option}>
                              {option}
                            </MenuItem>
                          ))}
                        </TextField>
                      ) : (
                        <TextField
                          {...field}
                          className={classes.textField}
                          label={attr.Field.label}
                          onChange={field.onChange}
                          error={invalid}
                          disabled={rejectNewUser}
                          helperText={invalid ? 'Required field' : ''}
                        />
                      )
                    }
                  />
                ))
              ) : (
                <Controller
                  name={item.field ? item.field : ''}
                  render={({ field, fieldState: { invalid } }) => (
                    <TextField
                      {...field}
                      className={classes.textField}
                      label={item.field}
                      rows={6}
                      onChange={field.onChange}
                      error={invalid}
                      autoComplete='fname'
                      disabled={rejectNewUser || item.field === 'email' || item.field === 'uid'}
                      helperText={invalid ? 'Required field' : ''}
                    />
                  )}
                  defaultValue={item.oldValue}
                  control={control}
                />
              )
            )}
          </Grid>

          {rejectNewUser && (
            <Controller
              name='reason'
              render={({ field, fieldState: { invalid } }) => (
                <TextField
                  {...field}
                  className={classes.textField}
                  label='Reason'
                  required
                  multiline
                  rows={4}
                  error={invalid}
                  autoComplete='fname'
                  helperText={invalid ? 'Required field' : ''}
                  inputProps={{ maxLength: 250 }}
                />
              )}
              control={control}
            />
          )}

          <Button className={classes.btn} type='submit' fullWidth variant='contained' color='primary'>
            Save Changes
          </Button>
          <Button
            variant='contained'
            color='default'
            className={classes.cancelButton}
            fullWidth
            onClick={() => {
              setRejectNewUser(false);
              handleClose();
            }}
          >
            Cancel
          </Button>
        </form>
      </Paper>
    </Modal>
  );
};
